import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AspectRatio, Box, Button, HStack, Skeleton, Stack, Text, useBreakpointValue, } from '@chakra-ui/react';
import { CategoryFilterMenu } from '@client/components/collections/CategoryFilterMenu';
import { SortableListingGrid } from '@client/components/collections/SortableListingGrid';
import { AppError } from '@client/components/feedback/AppError';
import { CountryFlagIcon } from '@client/components/icons/CountryFlagIcon';
import { AppImage } from '@client/components/imageDisplay/AppImage';
import { ImagePlaceholder } from '@client/components/imageDisplay/ImagePlaceholder';
import { RichTextDialog } from '@client/components/richText/RichTextDialog';
import { GRID_CONTENT_MAX_WIDTH, GRID_PAGE_PADDING_Y, STANDARD_GRID_GAP, } from '@client/constants';
import { useApiClient } from '@client/hooks/useApiClient';
import { useListingView } from '@client/hooks/useListingView';
import { useMinDuration } from '@client/hooks/useMinDuration';
import { useFavorites } from '@client/providers/FavoritesProvider';
import { displayFontFamily } from '@client/theme';
import { callApi } from '@client/utils/apiUtils';
import { shopProfileImageUrl } from '@client/utils/imageUtils';
import { useEffect, useState } from 'react';
import { FaHeart } from 'react-icons/fa';
import { FaShop } from 'react-icons/fa6';
import { useParams } from 'react-router-dom';
const titleFontSize = {
    base: 40,
    md: 60,
};
export const ShopPage = () => {
    const { id } = useParams();
    const [aboutOpen, setAboutOpen] = useState(false);
    const apiClient = useApiClient();
    const { isFavoritedShop, toggleFavoriteShop } = useFavorites();
    const [shopData, setShopData] = useState(null);
    const [shopDataLoading, setShopDataLoading] = useState(true);
    const [shopDataError, setShopDataError] = useState(null);
    const [listings, setListings] = useState([]);
    const [listingsLoading, setListingsLoading] = useState(true);
    const [listingsError, setListingsError] = useState(null);
    const responsiveBannerAspectRatio = useBreakpointValue({
        base: 1.5,
        md: 2.0,
        lg: 2.5,
        xl: 3.0,
    });
    useEffect(() => {
        setShopDataLoading(true);
        setShopDataError(null);
        setListingsLoading(true);
        setListingsError(null);
        loadShopData();
        loadListings();
    }, [id]);
    const loadShopData = async () => {
        const result = await callApi(apiClient.shops.getById({ params: { id: id } }));
        if (result.error !== null) {
            setShopDataError(result.error);
        }
        else {
            setShopData(result.data);
        }
        setShopDataLoading(false);
    };
    const loadListings = async () => {
        const result = await callApi(apiClient.shops.getListings({ params: { id: id } }));
        if (result.error !== null) {
            setListingsError(result.error);
        }
        else {
            setListings(result.data.listings);
        }
        setListingsLoading(false);
    };
    const listingView = useListingView(listings);
    const isLoading = useMinDuration(shopDataLoading || listingsLoading);
    if (shopDataError) {
        return (_jsx(AppError, { title: "Failed to shop data", content: shopDataError }));
    }
    return (_jsxs(_Fragment, { children: [isLoading && (_jsx(AspectRatio, { ratio: responsiveBannerAspectRatio, children: _jsx(Skeleton, { width: "100%" }) })), !isLoading && (_jsxs(Box, { position: "relative", boxShadow: "md", children: [_jsx(Box, { mx: "auto", children: shopData?.profileImageUuid ? (_jsx(AppImage, { aspectRatio: responsiveBannerAspectRatio, imageProps: {
                                src: shopProfileImageUrl(shopData.profileImageUuid),
                            } })) : (_jsx(ImagePlaceholder, { aspectRatio: responsiveBannerAspectRatio })) }), _jsxs(Stack, { position: "absolute", bottom: { base: 5, md: 10 }, left: { base: 5, md: 10 }, gap: 5, alignItems: "flex-start", children: [_jsxs(Stack, { gap: 1, textShadow: "0 1px 2px rgba(0, 0, 0, 0.65), 0 2px 4px rgba(0, 0, 0, 0.5), 0 4px 8px rgba(0, 0, 0, 0.35), 0 8px 16px rgba(0, 0, 0, 0.2);", color: "#FFF", children: [_jsx(Text, { display: "block", fontSize: titleFontSize, fontWeight: 700, lineHeight: 1, fontFamily: displayFontFamily, children: shopData?.title }), _jsx(Text, { display: "block", fontSize: Object.entries(titleFontSize).reduce((acc, [key, value]) => ({
                                            ...acc,
                                            [key]: value * 0.65,
                                        }), {}), fontWeight: 600, fontFamily: displayFontFamily, children: shopData?.classification }), _jsxs(HStack, { gap: { base: 3, md: 4 }, children: [_jsx(Box, { width: {
                                                    base: 6,
                                                    md: 8,
                                                    lg: 9,
                                                }, children: _jsx(CountryFlagIcon, { countryCode: shopData?.countryCode, svgProps: {
                                                        style: {
                                                            filter: 'drop-shadow( 1px 1px 2px rgba(0, 0, 0, .7))',
                                                        },
                                                    } }) }), _jsx(Text, { fontWeight: 500, fontSize: Object.entries(titleFontSize).reduce((acc, [key, value]) => ({
                                                    ...acc,
                                                    [key]: value * 0.55,
                                                }), {}), children: shopData?.location })] })] }), _jsxs(HStack, { gap: 2, children: [shopData?.profileRichText && (_jsx(ActionButton, { icon: FaShop, label: "About", onClick: () => setAboutOpen(true) })), _jsx(ActionButton, { icon: FaHeart, label: shopData &&
                                            isFavoritedShop(shopData.shortId)
                                            ? 'Favorited'
                                            : 'Favorite', iconColor: shopData &&
                                            isFavoritedShop(shopData.shortId)
                                            ? 'red.600'
                                            : undefined, onClick: () => shopData &&
                                            toggleFavoriteShop(shopData) })] })] })] })), shopData?.profileRichText && (_jsx(RichTextDialog, { title: shopData.title, open: aboutOpen, onClose: () => setAboutOpen(false), htmlString: shopData.profileRichText })), listingsError ? (_jsx(AppError, { title: "Failed to load listings", content: listingsError })) : (_jsx(Box, { py: GRID_PAGE_PADDING_Y, px: STANDARD_GRID_GAP, width: "100%", maxW: GRID_CONTENT_MAX_WIDTH, mx: "auto", children: _jsx(SortableListingGrid, { view: listingView, isLoading: isLoading, headerProps: { mb: 1 }, heading: (isLoading ||
                        listingView.category.options.length >
                            0) && (_jsx(CategoryFilterMenu, { ...listingView.category })) }) }))] }));
};
const ActionButton = (props) => (_jsxs(Button, { size: "md", variant: "subtle", onClick: props.onClick, children: [_jsx(Box, { color: props.iconColor, children: _jsx(props.icon, {}) }), props.label] }));
